// bluaIQ — M4: Contractor Bids Module
// Upload bids, AI reads them, scope gap analysis

import { apiFetch } from '../utils/api'
import { friendlyApiError, isPlanGateStatus } from '../utils/apiErrors'
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, AlertTriangle, CheckCircle, XCircle, DollarSign, Loader2, FileText, Info, MessageSquare, X, Copy, Check, FilePlus } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { formatMoney, formatMoneyValue } from '../utils/money'
import { localizeRiskNote, ORIGINAL_EXTRACTED_TEXT_LABEL } from '../utils/bidDecisionLabels'
import { buildClarificationDraft } from '../utils/bidClarification'
import ContactLinker, { type LinkedContact } from './ContactLinker'

// ── TYPES ─────────────────────────────────────────

// Per-line item from AI bid extraction (PR #208 BidReadResult.breakdown).
interface BidLineItem {
  description: string
  optionLabel?: string   // group label for options/alternates, e.g. "Option A – Electric"
  quantity?: number
  unit?: string
  unitPrice?: number
  subtotal?: number
}

// Typed subset of BidReadResult stored in the rawAiResponse Json? column.
// Kept local (not imported from claudeService) to avoid FE/BE coupling.
// All fields optional/nullable so bids uploaded before PR #208 degrade
// gracefully — the expanded card sections that depend on raw are gated
// on `bid.rawAiResponse != null`.
interface BidRawAi {
  pricingType?: 'single' | 'options' | 'alternates' | 'combined' | null
  breakdown?: BidLineItem[]
  depositPercent?: number | null   // e.g. 30 = "30% deposit required"
  depositAmount?: number | null    // e.g. 3150 = "$3,150 down payment"
  paymentMethod?: string | null    // e.g. "check", "zelle", "cash"
  drawSchedule?: string | null     // e.g. "33% start, 33% mid, 34% final"
  availableDate?: string | null    // YYYY-MM-DD start date
  validUntil?: string | null       // YYYY-MM-DD bid expiration
  warrantyMonths?: number | null
  licenseNumber?: string | null
  insuranceMentioned?: boolean
  fixturesIncluded?: boolean | null  // distinct from materialsIncluded
  allowances?: string[]              // e.g. ["$2,000 fixture allowance"]
  redFlags?: string[]                // AI-extracted risk flags
}

interface Bid {
  id: string
  contractorName: string
  trade: string
  totalAmount: number
  laborOnly: boolean
  materialsIncluded: boolean
  scopeIncluded: string[]
  scopeExcluded: string[]
  timelineDays?: number
  notes?: string
  status: string
  scopeGaps?: { gap: string; severity: string }[]
  createdAt: string
  // Source file preservation (PR #206)
  storagePath?: string | null
  originalFilename?: string | null
  mimeType?: string | null
  // AI extraction result stored as Json? in the DB (PR #208).
  // Null / absent for bids uploaded before PR #208 was deployed.
  rawAiResponse?: BidRawAi | null
  // NBA-3A: durable bid-decision state (separate from the upload/import `status`).
  // Absent on bids created before the NBA-3A migration → treated as 'PENDING'.
  decisionStatus?: string | null // PENDING | SELECTED | NOT_SELECTED | REJECTED
  decisionAt?: string | null
  decisionBy?: string | null
  decisionNotes?: string | null
  riskAcknowledgedAt?: string | null
  riskAcknowledgedBy?: string | null
  // Contacts Linking Foundation — durable contact link + forward-compatible
  // extracted contact holders (NOT populated yet; no AI-extraction expansion).
  contractorContactId?: string | null
  contractorContact?: LinkedContact | null
  extractedEmail?: string | null
  extractedPhone?: string | null
  // NBA-4 blocker fix: computed read-only field from the bids GET response;
  // true when a DRAFT contract with sourceType=CONTRACTOR_BID exists for this bid.
  hasDraftContract?: boolean
}

// ── DISPLAY HELPERS (PR C) ─────────────────────────
// Client-side display helpers for readability polish.
// No backend, claudeService, schema, or migration changes.

// Category for AI red flags — matches the 6 BID_PROMPT-driven flag types.
type FlagCategory = 'critical' | 'payment' | 'scope' | 'timing' | 'ambiguous' | 'missing'

// Per-category rendering metadata: labels (EN/ES) and Tailwind color classes.
interface FlagGroupMeta {
  category: FlagCategory
  labelEn: string
  labelEs: string
  headerColorClass: string  // category header text color
  flagColorClass: string    // individual flag text color
}

// Order matters: critical first, muted "missing" last.
const FLAG_CATEGORY_META: FlagGroupMeta[] = [
  { category: 'critical',  labelEn: 'Critical',            labelEs: 'Crítico',              headerColorClass: 'text-red-600',   flagColorClass: 'text-red-700'   },
  { category: 'payment',   labelEn: 'Payment risk',        labelEs: 'Riesgo de pago',       headerColorClass: 'text-amber-600', flagColorClass: 'text-amber-700' },
  { category: 'scope',     labelEn: 'Scope risk',          labelEs: 'Riesgo de alcance',    headerColorClass: 'text-amber-600', flagColorClass: 'text-amber-700' },
  { category: 'timing',    labelEn: 'Short validity',      labelEs: 'Vigencia corta',       headerColorClass: 'text-amber-600', flagColorClass: 'text-amber-700' },
  { category: 'ambiguous', labelEn: 'Ambiguous pricing',   labelEs: 'Precio ambiguo',       headerColorClass: 'text-amber-600', flagColorClass: 'text-amber-700' },
  { category: 'missing',   labelEn: 'Missing information', labelEs: 'Información faltante', headerColorClass: 'text-[#9E9890]', flagColorClass: 'text-[#9E9890]' },
]

// Client-side construction-term dictionary for common EN→ES scope items.
// BID_PROMPT extracts scope in English; known terms are translated at
// display time for Spanish UI users. Unknown technical terms (PEX, LVL,
// vent stack, etc.) are preserved as-is — never guessed at.
const CONSTRUCTION_TERMS_ES: Record<string, string> = {
  'Rough-in': 'Preparación (rough-in)',
  'Finish': 'Acabados',
  'Trim': 'Molduras',
  'Framing': 'Estructura',
  'Drywall': 'Tablaroca',
  'Insulation': 'Aislante',
  'Flooring': 'Pisos',
  'Roofing': 'Techo',
  'Foundation': 'Cimentación',
  'Electrical': 'Electricidad',
  'Plumbing': 'Plomería',
  'Painting': 'Pintura',
  'Cabinets': 'Gabinetes',
  'Countertops': 'Encimeras',
  'Windows': 'Ventanas',
  'Doors': 'Puertas',
  'Fixtures': 'Accesorios',
  'Gas line': 'Línea de gas',
  'Gas Line': 'Línea de gas',
  'Sewer tap': 'Conexión al drenaje',
  'Sewer Tap': 'Conexión al drenaje',
  'Water heater': 'Calentador de agua',
  'Water Heater': 'Calentador de agua',
  'Water heater install': 'Instalación de calentador',
  'Water Heater Install': 'Instalación de calentador',
  'Permits': 'Permisos',
  'Demo': 'Demolición',
  'Cleanup': 'Limpieza',
  'HVAC': 'Climatización',
}

// Translate a scope item string for display.
// Exact match in CONSTRUCTION_TERMS_ES → returns Spanish label.
// Anything else → returns original (preserves technical terms as-is).
function translateScopeItem(item: string, isEs: boolean): string {
  if (!isEs) return item
  return CONSTRUCTION_TERMS_ES[item] ?? item
}

// Display-time map for common AI-extracted payment method values.
const PAYMENT_METHOD_ES: Record<string, string> = {
  'check': 'Cheque',
  'cash': 'Efectivo',
  'credit card': 'Tarjeta de crédito',
  'zelle': 'Zelle',
  'venmo': 'Venmo',
  'wire': 'Transferencia bancaria',
  'wire transfer': 'Transferencia bancaria',
}

// Translate a payment method value for display.
// Matched case-insensitively; unknown values preserved as-is.
function translatePaymentMethod(method: string, isEs: boolean): string {
  if (!isEs) return method
  return PAYMENT_METHOD_ES[method.toLowerCase().trim()] ?? method
}

// Classify an AI red flag string into one of the 6 BID_PROMPT-driven categories.
// Pattern matching is reliable because the AI uses prompt-consistent phrasing.
// Unknown patterns fall to 'missing' (muted gray) rather than alarming red.
function classifyFlag(flag: string): FlagCategory {
  const f = flag.toLowerCase()
  if (/insurance|gl\b|wc\b|liability/.test(f))                             return 'critical'
  if (/deposit.*40|40.*deposit|upfront/.test(f))                            return 'payment'
  if (/below.*market|market.*below|incomplete.*scope|many.*exclusion/.test(f)) return 'scope'
  if (/bid validity|valid.*\d+\s*day|\d+\s*day.*valid|validity.*week/.test(f)) return 'timing'
  if (/ambiguous|misread|combined.*option|option.*combined/.test(f))        return 'ambiguous'
  return 'missing'
}

// Build a 2–3 sentence executive summary from structured bid fields.
// Uses only existing Bid + BidRawAi values — never invents data.
// tradeName: i18n-translated trade label (e.g. "Plumbing" / "Plomería").
// When raw is null (old bids), returns a single sentence with total only.
function buildExecutiveSummary(bid: Bid, raw: BidRawAi | null, isEs: boolean, tradeName: string): string {
  const sentences: string[] = []

  // S1: trade + total + materials status
  const materialsEn = bid.materialsIncluded
    ? 'Materials and labor included.'
    : bid.laborOnly ? 'Labor only.' : 'Materials not specified.'
  const materialsEs = bid.materialsIncluded
    ? 'Incluye labor y materiales.'
    : bid.laborOnly ? 'Solo mano de obra.' : 'Sin materiales especificados.'
  const totalStr = formatMoneyValue(bid.totalAmount ?? null, isEs)
  sentences.push(
    isEs
      ? `Propuesta de ${tradeName.toLowerCase()} por ${totalStr}. ${materialsEs}`
      : `${tradeName} bid for ${totalStr}. ${materialsEn}`
  )

  // S2: payment summary or "Not stated" / "No especificado" fallback
  if (raw) {
    const hasDeposit = raw.depositPercent != null || raw.depositAmount != null
    const depStr = hasDeposit
      ? [
          raw.depositPercent != null ? `${raw.depositPercent}%` : null,
          raw.depositAmount   != null ? formatMoneyValue(raw.depositAmount, isEs) : null,
        ].filter(Boolean).join(' / ')
      : null

    if (isEs) {
      if (raw.drawSchedule) {
        sentences.push(raw.drawSchedule + '.')
      } else if (depStr && raw.paymentMethod) {
        sentences.push(`Depósito ${depStr}, pago por ${translatePaymentMethod(raw.paymentMethod, true)}.`)
      } else if (depStr) {
        sentences.push(`Depósito: ${depStr}.`)
      } else if (raw.paymentMethod) {
        sentences.push(`Pago: ${translatePaymentMethod(raw.paymentMethod, true)}.`)
      } else {
        sentences.push('Condiciones de pago: No especificado.')
      }
    } else {
      if (raw.drawSchedule) {
        sentences.push('Payment: ' + raw.drawSchedule + '.')
      } else if (depStr && raw.paymentMethod) {
        sentences.push(`Payment: ${depStr} deposit via ${translatePaymentMethod(raw.paymentMethod, false)}.`)
      } else if (depStr) {
        sentences.push(`Deposit: ${depStr}.`)
      } else if (raw.paymentMethod) {
        sentences.push(`Payment: ${translatePaymentMethod(raw.paymentMethod, false)}.`)
      } else {
        sentences.push('Payment terms: Not stated.')
      }
    }
  }

  // S3: timeline and expiration (when available)
  const timeParts: string[] = []
  if (bid.timelineDays) {
    timeParts.push(isEs ? `Plazo ${bid.timelineDays} días` : `Timeline: ${bid.timelineDays} days`)
  }
  if (raw?.validUntil) {
    timeParts.push(isEs ? `vigente hasta ${raw.validUntil}` : `valid until ${raw.validUntil}`)
  }
  if (timeParts.length > 0) {
    const combined = timeParts.join(', ')
    sentences.push(combined.charAt(0).toUpperCase() + combined.slice(1) + '.')
  }

  return sentences.join(' ')
}

// ── NBA-1: BID DECISION SUPPORT ──────────────────────────────────────────────
// Derives a decision summary from EXISTING bid data only.
// No downstream writes, no new API calls, no schema changes.

type RiskLevel = 'high' | 'medium' | 'low' | 'needs_review'

interface BidDecision {
  riskLevel: RiskLevel
  isComparable: boolean
  missingFields: string[]
  recommendedAction: 'ask_clarification' | 'review_scope' | 'comparable'
}

const MISSING_FIELD_LABELS: Record<string, { en: string; es: string }> = {
  scope:         { en: 'Scope',             es: 'Alcance'             },
  payment_terms: { en: 'Payment terms',     es: 'Condiciones de pago' },
  warranty:      { en: 'Warranty',          es: 'Garantía'            },
  schedule:      { en: 'Schedule/Duration', es: 'Plazo/Duración'      },
}

const RECOMMENDED_ACTION_COPY: Record<string, { en: string; es: string }> = {
  ask_clarification: {
    en: 'Ask for clarification before creating a contract.',
    es: 'Pedir aclaración antes de crear un contrato.',
  },
  review_scope: {
    en: 'Scope is unclear — request a detailed breakdown before comparing.',
    es: 'El alcance no está claro — solicita un desglose detallado antes de comparar.',
  },
  comparable: {
    en: 'Ready to compare. Verify scope and terms with the original document.',
    es: 'Lista para comparar. Verifica el alcance y condiciones con el documento original.',
  },
}

const FUTURE_ACTIONS: { key: string; en: string; es: string }[] = [
  { key: 'ask_clarification', en: 'Ask for clarification',            es: 'Pedir aclaración'               },
  { key: 'mark_selected',     en: 'Mark as selected',                 es: 'Marcar como seleccionada'       },
  { key: 'create_contract',   en: 'Create draft contract',            es: 'Crear borrador de contrato'     },
  { key: 'add_payments',      en: 'Add payments to schedule',         es: 'Agregar pagos al calendario'    },
  { key: 'add_milestones',    en: 'Add milestones to Calendar/Gantt', es: 'Agregar hitos al calendario/Gantt' },
  { key: 'update_budget',     en: 'Update Budget',                    es: 'Actualizar presupuesto'           },
  { key: 'create_contact',    en: 'Create/update Contact',            es: 'Crear/actualizar contacto'      },
  { key: 'keep_comparison',   en: 'Keep for comparison',              es: 'Mantener para comparación'      },
  { key: 'reject',            en: 'Reject',                           es: 'Rechazar'                       },
]

const RISK_LEVEL_DISPLAY: Record<RiskLevel, { en: string; es: string; colorClass: string }> = {
  high:         { en: 'High risk',    es: 'Riesgo alto',      colorClass: 'bg-red-50 text-red-600'     },
  medium:       { en: 'Needs clarification', es: 'Requiere aclaración', colorClass: 'bg-amber-50 text-amber-700' },
  low:          { en: 'Low risk',     es: 'Riesgo bajo',      colorClass: 'bg-green-50 text-green-700' },
  needs_review: { en: 'Needs review', es: 'Requiere revisión', colorClass: 'bg-orange-50 text-orange-600' },
}

function deriveBidDecision(bid: Bid, raw: BidRawAi | null): BidDecision {
  const hasTotalAmount = bid.totalAmount != null && Number.isFinite(bid.totalAmount) && bid.totalAmount > 0
  const hasScope       = bid.scopeIncluded.length > 0
  const hasTrade       = !!bid.trade && bid.trade.trim() !== ''

  const hasPaymentTerms = raw != null && (
    raw.depositPercent != null || raw.depositAmount != null ||
    raw.paymentMethod  != null || raw.drawSchedule  != null
  )
  const hasWarranty = raw != null && raw.warrantyMonths != null
  const hasSchedule = bid.timelineDays != null ||
    (raw != null && (raw.availableDate != null || raw.validUntil != null))

  const missingFields: string[] = []
  if (!hasScope)        missingFields.push('scope')
  if (!hasPaymentTerms) missingFields.push('payment_terms')
  if (!hasWarranty)     missingFields.push('warranty')
  if (!hasSchedule)     missingFields.push('schedule')

  const redFlags = raw?.redFlags ?? []
  const hasCriticalFlags = redFlags.some(f => classifyFlag(f) === 'critical')
  const hasPaymentRisk   = redFlags.some(f => classifyFlag(f) === 'payment')

  let riskLevel: RiskLevel
  if (!hasTotalAmount || !hasTrade) {
    riskLevel = 'needs_review'
  } else if (hasCriticalFlags) {
    riskLevel = 'high'
  } else if (missingFields.length >= 2 || hasPaymentRisk) {
    riskLevel = 'medium'
  } else if (missingFields.length === 1) {
    riskLevel = 'medium'
  } else {
    riskLevel = 'low'
  }

  const isComparable = hasTotalAmount && hasScope

  let recommendedAction: BidDecision['recommendedAction']
  if (!hasTotalAmount || !hasTrade) {
    recommendedAction = 'ask_clarification'
  } else if (!hasScope) {
    recommendedAction = 'review_scope'
  } else if (hasCriticalFlags || missingFields.length >= 1) {
    recommendedAction = 'ask_clarification'
  } else {
    recommendedAction = 'comparable'
  }

  return { riskLevel, isComparable, missingFields, recommendedAction }
}

// ── NBA-2: ASK FOR CLARIFICATION (draft only) ────────────────────────────────
// Activates the first real action from the decision shell. Produces a
// reviewable clarification DRAFT (FE-only) generated from the bid's
// missing/unclear info. It NEVER sends an email, creates a contact, or writes
// to Contracts / Budget / Payments / Calendar/Gantt. Contractor bids carry no
// email field (see schema), so the recipient is always missing → the draft
// surfaces a "no contractor email found" warning and the save/send affordances
// stay disabled. The user can still copy the text manually.

// All NBA-2 user-facing copy, bilingual. Inline EN/ES (matches NBA-1 pattern).
const NBA2_COPY = {
  askForClarification:   { en: 'Ask for clarification',        es: 'Pedir aclaración' },
  clarificationDraft:    { en: 'Clarification draft',          es: 'Borrador de aclaración' },
  reviewBeforeSending:   { en: 'Review before sending',        es: 'Revisar antes de enviar' },
  missingInfoToRequest:  { en: 'Missing information to request', es: 'Información faltante por solicitar' },
  noContractorEmail:     { en: 'No contractor email found',    es: 'No se encontró correo del contratista' },
  addSelectContact:      { en: 'Add/select a contact before sending', es: 'Agrega/selecciona un contacto antes de enviar' },
  copyMessage:           { en: 'Copy message',                 es: 'Copiar mensaje' },
  copied:                { en: 'Copied',                       es: 'Copiado' },
  editDraft:             { en: 'Edit draft',                   es: 'Editar borrador' },
  saveDraft:             { en: 'Save draft',                   es: 'Guardar borrador' },
  sendLater:             { en: 'Send later',                   es: 'Enviar después' },
  doNotSendAuto:         { en: 'Do not send automatically',    es: 'No enviar automáticamente' },
  noContract:            { en: 'This action does not create a contract', es: 'Esta acción no crea un contrato' },
  // NOTE: ES intentionally localizes "Budget"/"Calendar/Gantt" → "presupuesto"/
  // "calendario/Gantt" to match the NBA-1 FU (PR #339) Sandbox-verified fix and
  // the bilingual rule, instead of the literal mixed-language form in the spec.
  noBudgetPaymentsCal:   { en: 'This action does not update Budget, Payments, or Calendar/Gantt', es: 'Esta acción no actualiza el presupuesto, los pagos ni el calendario/Gantt' },
  responseReviewable:    { en: 'Contractor response can be reviewed later before creating a contract', es: 'La respuesta del contratista podrá revisarse después antes de crear un contrato' },
  subjectLabel:          { en: 'Subject',                      es: 'Asunto' },
  noClarificationNeeded: { en: 'No clarification needed — a generic confirmation draft is offered below.', es: 'No se requiere aclaración — abajo se ofrece un borrador genérico de confirmación.' },
} as const

// Derive extra clarification topics from AI red-flag categories + unknown
// fixtures. These are concrete "please clarify X" requests — never invented
// facts. Returns topic keys consumed by buildClarificationDraft().
function deriveClarificationTopics(raw: BidRawAi | null): string[] {
  const topics: string[] = []
  const redFlags = raw?.redFlags ?? []
  if (redFlags.some(f => classifyFlag(f) === 'critical'))  topics.push('insurance')
  if (redFlags.some(f => classifyFlag(f) === 'ambiguous')) topics.push('pricing')
  if (redFlags.some(f => classifyFlag(f) === 'timing'))    topics.push('expiration')
  // Whether fixtures are included is a common, concrete unknown worth asking.
  if (raw != null && raw.fixturesIncluded == null)         topics.push('materials_fixtures')
  return topics
}

// ── NBA-3A: DURABLE BID DECISION (Mark as selected / Reject) ─────────────────
// Activates the first DURABLE action from the decision shell. Persists a
// decisionStatus (PENDING | SELECTED | NOT_SELECTED | REJECTED) to ContractorBid
// via PATCH /projects/:id/bids/:bidId/decision. It does NOT create a contract,
// payment, budget, calendar or gantt task, or contact — selection is a builder
// decision only, and the full Bid → Contract flow remains a future step. The
// existing upload/import `status` (RECEIVED/ACCEPTED/REJECTED) is untouched.

// All NBA-3A user-facing copy, bilingual. Inline EN/ES (matches NBA-1/NBA-2).
const NBA3_COPY = {
  markAsSelected:        { en: 'Mark as selected',          es: 'Marcar como seleccionada' },
  selected:             { en: 'Selected',                  es: 'Seleccionada' },
  notSelected:          { en: 'Not selected',              es: 'No seleccionada' },
  reject:               { en: 'Reject',                    es: 'Rechazar' },
  rejected:             { en: 'Rejected',                  es: 'Rechazada' },
  keepForComparison:    { en: 'Keep for comparison',       es: 'Mantener para comparación' },
  selectionRequiresConfirmation: { en: 'Selection requires confirmation', es: 'La selección requiere confirmación' },
  rejectRequiresConfirmation:    { en: 'Rejection requires confirmation', es: 'El rechazo requiere confirmación' },
  bidHasMissingInfo:    { en: 'This bid has missing information', es: 'Esta propuesta tiene información faltante' },
  selectAnyway:         { en: 'Select anyway?',            es: '¿Seleccionarla de todos modos?' },
  riskAcknowledged:     { en: 'Risk acknowledged',         es: 'Riesgo reconocido' },
  noContract:           { en: 'This action does not create a contract', es: 'Esta acción no crea un contrato' },
  // ES localizes "Budget"/"Calendar/Gantt" to match the NBA-1/NBA-2 fixes.
  noBudgetPaymentsCal:  { en: 'This action does not update Budget, Payments, or Calendar/Gantt', es: 'Esta acción no actualiza el presupuesto, los pagos ni el calendario/Gantt' },
  bidContractFuture:    { en: 'Full Bid → Contract remains a future step', es: 'El flujo completo Propuesta → Contrato sigue pendiente' },
  rejectDoesNotDelete:  { en: 'Rejecting does not delete the bid — it stays for comparison', es: 'Rechazar no elimina la propuesta — se mantiene para comparación' },
  alreadySelectedInTrade: { en: 'Another bid in this trade is already selected. Selecting this one will not change the other.', es: 'Ya hay otra propuesta seleccionada en este oficio. Seleccionar esta no cambiará la otra.' },
  addDecisionNote:      { en: 'Add decision note',         es: 'Agregar nota de decisión' },
  decisionSaved:        { en: 'Decision saved',            es: 'Decisión guardada' },
  decisionFailed:       { en: 'Decision could not be saved', es: 'No se pudo guardar la decisión' },
  confirm:              { en: 'Confirm',                   es: 'Confirmar' },
  cancel:               { en: 'Cancel',                    es: 'Cancelar' },
  saving:               { en: 'Saving…',                   es: 'Guardando…' },
  // NBA-3B: decision-clarity polish.
  // Decision metadata (who / when). {by} and {date} are interpolated; a
  // date-only fallback is used when no human-readable identity is available.
  selectedByOn:         { en: 'Selected by {by} on {date}',   es: 'Seleccionada por {by} el {date}' },
  selectedOn:           { en: 'Selected on {date}',           es: 'Seleccionada el {date}' },
  rejectedByOn:         { en: 'Rejected by {by} on {date}',    es: 'Rechazada por {by} el {date}' },
  rejectedOn:           { en: 'Rejected on {date}',            es: 'Rechazada el {date}' },
  // Decision note (optional, never required).
  decisionNote:         { en: 'Decision note',             es: 'Nota de decisión' },
  optionalDecisionNote: { en: 'Optional decision note',    es: 'Nota de decisión opcional' },
  saveDecisionNote:     { en: 'Save decision note',        es: 'Guardar nota de decisión' },
  // Decision-state-only disclaimer (shown on the persisted card + in the modal).
  decisionStateOnly:    { en: 'Decision state only — no contract has been created', es: 'Solo estado de decisión — no se ha creado ningún contrato' },
  // NBA-4 blocker fix: shown on the card when a DRAFT contract already exists.
  // Must NOT claim the contract was sent, or that payments/Budget/Gantt updated.
  draftContractExists:  { en: 'Draft contract exists — not sent and no downstream modules updated.', es: 'Ya existe un borrador de contrato — no se ha enviado y no se actualizaron módulos posteriores.' },
  // Same-trade multiplicity: NBA-3B never auto-unselects siblings, so it states
  // plainly that more than one bid may stay selected until contract creation.
  multipleMaySelect:    { en: 'Multiple bids for this trade may be selected until final contract creation.', es: 'Puede haber varias propuestas seleccionadas para este oficio hasta crear el contrato final.' },
} as const

// ── NBA-4: CREATE DRAFT CONTRACT FROM SELECTED BID ─────────────────────────
// All user-facing copy for the "Create draft contract" flow. Inline EN/ES
// (same pattern as NBA-2/NBA-3A/NBA-3B). The action only appears for SELECTED
// bids. It creates EXACTLY ONE DRAFT contract — no payment, no Budget, no
// Calendar/Gantt, no Contact, no Send-for-Signature.
const NBA4_COPY = {
  createDraftContract:         { en: 'Create draft contract',                   es: 'Crear borrador de contrato'            },
  draftOnly:                   { en: 'This creates a draft contract only.',      es: 'Esto crea solo un borrador de contrato.' },
  doesNotSend:                 { en: 'This does not send the contract.',          es: 'Esto no envía el contrato.'             },
  doesNotCreatePayments:       { en: 'This does not create payments.',            es: 'Esto no crea pagos.'                    },
  doesNotUpdateBudget:         { en: 'This does not update Budget.',              es: 'Esto no actualiza el presupuesto.'       },
  doesNotCreateCalGantt:       { en: 'This does not create Calendar/Gantt events.', es: 'Esto no crea eventos en Calendario/Gantt.' },
  reviewBeforeSending:         { en: 'You can review and edit before sending.',   es: 'Puedes revisar y editar antes de enviar.' },
  sourceSelectedBid:           { en: 'Source: selected bid',                      es: 'Fuente: propuesta seleccionada'          },
  noLinkedContact:             { en: 'No contractor contact is linked. You can still create a draft, but link or select a contact before sending.', es: 'No hay un contacto de contratista enlazado. Puedes crear el borrador, pero enlaza o selecciona un contacto antes de enviarlo.' },
  linkedContactNoEmail:        { en: 'The linked contact has no email. You can still create a draft, but you must add an email before sending.', es: 'El contacto enlazado no tiene correo. Puedes crear el borrador, pero deberás agregar un correo antes de enviarlo.' },
  riskWarning:                 { en: 'This bid has missing information or risks. Review the draft carefully before sending.', es: 'Esta propuesta tiene información faltante o riesgos. Revisa el borrador con cuidado antes de enviarlo.' },
  multipleSelectedWarning:     { en: 'Multiple bids are selected for this trade. Only one draft contract will be created from this bid.', es: 'Hay varias propuestas seleccionadas para este oficio. Solo se creará un borrador de esta propuesta.' },
  confirm:                     { en: 'Confirm',                                   es: 'Confirmar'                               },
  cancel:                      { en: 'Cancel',                                    es: 'Cancelar'                                },
  creating:                    { en: 'Creating…',                                 es: 'Creando…'                                },
  draftCreated:                { en: 'Draft contract created.',                   es: 'Borrador de contrato creado.'            },
  draftReused:                 { en: 'A draft contract already exists for this bid.', es: 'Ya existe un borrador de contrato para esta propuesta.' },
  viewDraftContract:           { en: 'View draft contract',                       es: 'Ver borrador de contrato'                },
  draftFailed:                 { en: 'Could not create draft contract.',          es: 'No se pudo crear el borrador de contrato.' },
} as const

// Display metadata for a persisted decisionStatus badge. PENDING → no badge.
const DECISION_BADGE: Record<string, { en: string; es: string; colorClass: string }> = {
  SELECTED:     { en: 'Selected',     es: 'Seleccionada',   colorClass: 'bg-green-50 text-green-700' },
  REJECTED:     { en: 'Rejected',     es: 'Rechazada',      colorClass: 'bg-red-50 text-red-600'    },
  NOT_SELECTED: { en: 'Not selected', es: 'No seleccionada', colorClass: 'bg-[#F2F0EB] text-[#6B6660]' },
}

// NBA-3B: build the "Selected/Rejected by X on DATE" line from persisted decision
// metadata. `decisionBy` stores an opaque user id; we only show a "by {who}" form
// when it is human-readable (looks like an email). Otherwise we fall back to the
// date-only form — never an opaque id. Returns null when there is nothing to show.
function formatDecisionMeta(
  decisionStatus: string,
  decisionAt: string | null | undefined,
  decisionBy: string | null | undefined,
  isEs: boolean,
): string | null {
  if (decisionStatus !== 'SELECTED' && decisionStatus !== 'REJECTED') return null
  if (!decisionAt) return null
  const parsed = new Date(decisionAt)
  if (isNaN(parsed.getTime())) return null
  const date = parsed.toLocaleDateString(isEs ? 'es-ES' : 'en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
  const who = decisionBy && decisionBy.includes('@') ? decisionBy : null
  const pick = (k: keyof typeof NBA3_COPY) => (isEs ? NBA3_COPY[k].es : NBA3_COPY[k].en)
  if (decisionStatus === 'SELECTED') {
    return who
      ? pick('selectedByOn').replace('{by}', who).replace('{date}', date)
      : pick('selectedOn').replace('{date}', date)
  }
  return who
    ? pick('rejectedByOn').replace('{by}', who).replace('{date}', date)
    : pick('rejectedOn').replace('{date}', date)
}

// NBA-3B: consistent bilingual label for raw/original extracted proposal text.
// The source documents are typically English; when the UI is Spanish, that raw
// text is "in a different UI language", so we label it as the original extracted
// text (never translating or mutating the source). In English mode the raw text
// already matches the UI language, so no label is shown.
function OriginalExtractedLabel({ isEs }: { isEs: boolean }) {
  if (!isEs) return null
  return (
    <span
      data-testid="bid-original-extracted-label"
      className="block text-[8px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-0.5"
    >
      {ORIGINAL_EXTRACTED_TEXT_LABEL.es}
    </span>
  )
}

// ── COMPONENT ─────────────────────────────────────

export default function BidsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [selectedTrade, setSelectedTrade] = useState<string | null>(null)

  const { data: apiBids, isLoading } = useQuery<Bid[]>({
    // Include selectedTrade in the key so React Query re-fetches when the
    // trade filter changes — without this the cached all-bids result was
    // served for every trade selection.
    queryKey: ['bids', projectId, selectedTrade],
    queryFn: async () => {
      const token = await getToken()
      // Fix: when no trade is selected use GET /bids (returns all bids,
      // flat Bid[] array). When a trade is selected use GET /bids/:trade.
      // Previously this defaulted to /bids/all which hit the /:trade route
      // with trade='all', causing a Prisma 500 (invalid include) and always
      // returning an empty result even after fixing the include.
      const path = selectedTrade
        ? `/api/projects/${projectId}/bids/${selectedTrade}`
        : `/api/projects/${projectId}/bids`
      return apiFetch(path, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
        // PR3 (C7): a plan gate (402) is not a failure — the upgrade card
        // already explains it; show an empty list, not an error.
        if (isPlanGateStatus(r.status)) return []
        if (!r.ok) throw new Error('API unavailable')
        return r.json()
      })
    },
    enabled: !!projectId,
    retry: false,
  })

  // NBA-2: read-only project name for the clarification draft ("…proposal for
  // {projectName}"). Read-only GET — no writes. Project name is optional in the
  // draft (the clause is omitted when unavailable), so failures degrade safely.
  const { data: project } = useQuery<{ id: string; name: string }>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error(`API unavailable ${res.status}`)
      return res.json()
    },
    enabled: !!projectId,
    retry: false,
  })
  const projectName = project?.name ?? null

  // PR-NEW: honest-empty-states. Demo projects (DEMO_PROJECT_IDS in
  // src/utils/demoData.ts) keep their preloaded fallback bids so the
  // demo experience stays populated. Real projects with no bids must
  // render an honest empty state instead — no Torres/DFW/Reliable
  // placeholder names leaking into a real builder's surface.
  const isDemo = isDemoProject(projectId)
  const DEMO_BIDS: Bid[] = [
    { id: 'bid1', contractorName: 'Torres Plumbing', trade: 'plumbing', totalAmount: 14500, laborOnly: false, materialsIncluded: true, scopeIncluded: ['Rough-in', 'Finish', 'Water heater install'], scopeExcluded: ['Gas line', 'Sewer tap'], timelineDays: 12, status: 'RECEIVED', notes: 'Incluye PEX y PVC', createdAt: '2026-03-10' },
    { id: 'bid2', contractorName: 'DFW Plumbing Pro', trade: 'plumbing', totalAmount: 16200, laborOnly: true, materialsIncluded: false, scopeIncluded: ['Rough-in', 'Finish', 'Gas line', 'Water heater'], scopeExcluded: ['Sewer tap', 'Fixtures'], timelineDays: 10, status: 'RECEIVED', notes: 'Solo mano de obra', createdAt: '2026-03-12' },
    { id: 'bid3', contractorName: 'Reliable Pipes LLC', trade: 'plumbing', totalAmount: 13800, laborOnly: false, materialsIncluded: true, scopeIncluded: ['Rough-in', 'Finish'], scopeExcluded: ['Gas line', 'Water heater', 'Sewer tap'], timelineDays: 15, status: 'RECEIVED', notes: 'Precio más bajo pero scope limitado', createdAt: '2026-03-14' },
  ]
  const bids: Bid[] = Array.isArray(apiBids)
    ? apiBids
    : (isDemo ? DEMO_BIDS : [])

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken()
      setUploading(true)
      setUploadError(null)
      setUploadSuccess(null)
      // Client timeout so a slow/hung AI read can NEVER leave the button
      // stuck on "Uploading…" forever.
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 90_000)
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await apiFetch(`/api/projects/${projectId}/bids/read`, {
          method: 'POST',
          headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          body: formData,
          signal: controller.signal,
        })
        const data = await res.json().catch(() => ({}))
        // A non-OK response (invalid amount, AI limit, server error) must
        // surface as an error — NOT silently fall through to onSuccess.
        if (!res.ok) throw new Error((data as any)?.error || `upload_failed_${res.status}`)
        return data
      } finally {
        // ALWAYS reset the upload state — success, error, abort, or timeout.
        clearTimeout(timeout)
        setUploading(false)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bids', projectId] })
      setUploadSuccess(isEs ? 'Propuesta leída y guardada.' : 'Bid read and saved.')
    },
    onError: (err: any) => {
      const generic = isEs ? 'No se pudo leer la propuesta. Intenta de nuevo.' : 'Could not read the bid. Please try again.'
      const msg = err?.name === 'AbortError'
        ? (isEs ? 'La lectura tardó demasiado. Intenta de nuevo.' : 'Reading took too long. Please try again.')
        : friendlyApiError(err?.message, isEs, generic)
      setUploadError(msg)
    },
  })

  const trades = [...new Set(bids?.map(b => b.trade) || [])]

  const tradeName = (trade: string) => t(`bids.trade.${trade.toLowerCase()}`, { defaultValue: trade })

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Contractor Bids')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {bids?.length || 0} {t('bids received')}
          </p>
        </div>
      </div>

      {/* Upload bid — wired to existing POST /api/projects/:id/bids/read (PRO route).
          The module is already behind ProFeatureGate in App.tsx so no secondary
          plan gate is needed here. The "Available with subscription" badge has
          been removed — it was misleading for PRO users who already have access. */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) uploadMutation.mutate(file)
          // Reset so the same file can be re-uploaded after a correction.
          e.target.value = ''
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full border-2 border-dashed border-[#ECEAE4] rounded-xl p-6 text-center hover:border-[#06539B] hover:bg-[#EFF6FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <div className="flex flex-col items-center gap-2">
          {uploading
            ? <Loader2 size={28} className="text-[#06539B] animate-spin" />
            : <Upload size={28} className="text-[#9E9890]" />}
          <span className="text-[12px] font-[700]">
            {uploading
              ? (isEs ? 'Subiendo…' : 'Uploading…')
              : t('Upload contractor bid')}
          </span>
          {!uploading && (
            <span className="text-[10px] font-[600] text-[#9E9890]">PDF, JPG, PNG</span>
          )}
        </div>
      </button>
      {uploadError && (
        <p data-testid="bid-upload-error" className="text-[11px] font-[600] text-red-600 text-center -mt-1">{uploadError}</p>
      )}
      {uploadSuccess && !uploadError && (
        <p data-testid="bid-upload-success" className="text-[11px] font-[700] text-emerald-700 text-center -mt-1">{uploadSuccess}</p>
      )}

      {/* Financial Trust P0 (PR-F5): AI-extraction + not-connected honesty note */}
      <div
        data-testid="bids-trust-note"
        className="flex items-start gap-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2.5"
      >
        <Info size={13} className="text-[#9E9890] mt-0.5 shrink-0" />
        <p className="text-[10px] font-[600] text-[#6B6660] leading-relaxed">
          {isEs
            ? 'Los montos de propuestas son extraídos por IA de los documentos cargados. Verifica con el original antes de usarlos. Las propuestas aún no están conectadas a Contratos, Presupuesto ni Pagos.'
            : 'Bid amounts are AI-extracted from uploaded documents. Verify against the original before use. Bids are not yet connected to Contracts, Budget, or Payments.'}
        </p>
      </div>

      {/* Trade filter pills */}
      {trades.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedTrade(null)}
            className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${!selectedTrade ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}
          >
            {t('All')}
          </button>
          {trades.map(trade => (
            <button
              key={trade}
              onClick={() => setSelectedTrade(trade)}
              className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${selectedTrade === trade ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}
            >
              {tradeName(trade)}
            </button>
          ))}
        </div>
      )}

      {/* Bid cards */}
      <div className="space-y-3">
        {(selectedTrade ? bids?.filter(b => b.trade === selectedTrade) : bids)?.map(bid => (
          <BidCard key={bid.id} bid={bid} projectId={projectId} projectName={projectName} siblingBids={bids} />
        ))}
      </div>

      {(!bids || bids.length === 0) && !isLoading && (
        <div className="text-center py-12" data-testid="bids-honest-empty-state">
          <FileText size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          {/* PR-NEW: bilingual honest empty-state copy verbatim from the
              real-flow audit spec. Replaces the previous `t('No bids yet')`
              keys (which mapped to translations that did not match the
              product wording the task spec asked for). Real projects with
              zero bids now see this consistent message instead of the
              hardcoded Torres / DFW / Reliable demo placeholders that
              used to leak into the real-project surface. */}
          <p className="text-[12px] font-[700] text-[#1C1A17]">
            {isEs
              ? 'Aún no se han subido propuestas de contratistas. Sube una propuesta para empezar.'
              : 'No contractor bids uploaded yet. Upload a bid to get started.'}
          </p>
        </div>
      )}
    </div>
  )
}

// ── RISK NOTE TEXT (NBA-1 follow-up) ──────────────
// Renders an AI/system risk or scope-gap note honestly per the active UI
// language. In Spanish mode, known themes are shown as a Spanish interpretation;
// notes that cannot be safely localized show the ORIGINAL extracted text under a
// clearly-labeled wrapper so source evidence stays visually distinct. In English
// mode the original note is shown as-is. The stored AI text is never mutated.
function RiskNoteText({ note, isEs, colorClass }: { note: string; isEs: boolean; colorClass: string }) {
  const localized = isEs ? localizeRiskNote(note) : null
  if (localized) {
    return <span className={`text-[10px] font-[600] leading-tight ${colorClass}`}>{localized}</span>
  }
  // English mode → original note as-is. Spanish mode without a safe
  // localization → original note under a clear "original extracted text" label.
  if (!isEs) {
    return <span className={`text-[10px] font-[600] leading-tight ${colorClass}`}>{note}</span>
  }
  return (
    <span className="leading-tight" data-testid="bid-risk-note-original">
      <span className="block text-[8px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
        {ORIGINAL_EXTRACTED_TEXT_LABEL.es}
      </span>
      <span className={`text-[10px] font-[600] italic ${colorClass}`}>{note}</span>
    </span>
  )
}

// ── BID CARD ──────────────────────────────────────

function BidCard({ bid, projectId, projectName, siblingBids }: { bid: Bid; projectId: string | undefined; projectName: string | null; siblingBids: Bid[] }) {
  const { t, i18n } = useTranslation()
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'

  // Derive rawAiResponse fields upfront so they are available in both
  // the collapsed header (pricingType badge) and the expanded sections.
  const raw = bid.rawAiResponse ?? null
  const breakdown = raw?.breakdown ?? []
  const redFlags  = raw?.redFlags  ?? []
  const allowances = raw?.allowances ?? []
  const pricingType = raw?.pricingType ?? null
  // Bilingual "Not stated" shown for null fields on bids that were
  // explicitly parsed but lacked a given piece of information.
  const notStated = isEs ? 'No especificado' : 'Not stated'

  // PR C: payment/scheduling emptiness guards — suppress per-row
  // "Not stated" noise when ALL fields in a section are null.
  const hasAnyPayment = raw != null && (
    raw.depositPercent != null || raw.depositAmount != null ||
    raw.paymentMethod  != null || raw.drawSchedule  != null
  )
  const hasAnyScheduling = raw != null && (
    bid.timelineDays != null || raw.availableDate != null || raw.validUntil != null
  )

  // PR C: group AI red flags by category for tiered visual severity.
  // FLAG_CATEGORY_META order determines display order (critical first).
  const groupedFlags = FLAG_CATEGORY_META
    .map(meta => ({
      ...meta,
      label: isEs ? meta.labelEs : meta.labelEn,
      flags: redFlags.filter(flag => classifyFlag(flag) === meta.category),
    }))
    .filter(group => group.flags.length > 0)

  // i18n-translated trade label for the executive summary.
  const tradeDisplayName = t(`bids.trade.${bid.trade.toLowerCase()}`, { defaultValue: bid.trade })

  const decision = deriveBidDecision(bid, raw)

  const [expanded, setExpanded] = useState(false)
  const [openingFile, setOpeningFile] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  // NBA-2: clarification draft modal (FE-only, no send / no write).
  const [showClarification, setShowClarification] = useState(false)

  // NBA-3A: durable decision (Mark as selected / Reject). The modal is opened in
  // one of two intents; null = closed. A separate success flash mirrors the
  // upload-success pattern (no native dialogs).
  const [decisionIntent, setDecisionIntent] = useState<'SELECTED' | 'REJECTED' | null>(null)
  const [decisionFlash, setDecisionFlash] = useState<string | null>(null)
  const [decisionError, setDecisionError] = useState<string | null>(null)

  // Current persisted decision (defaults to PENDING for pre-migration bids).
  const decisionStatus = bid.decisionStatus ?? 'PENDING'
  // Non-blocking warning: is a DIFFERENT bid in the same trade already selected?
  // NBA-3A never auto-rejects others — this only informs the builder.
  const anotherSelectedInTrade = siblingBids.some(
    b => b.id !== bid.id && b.trade === bid.trade && (b.decisionStatus ?? 'PENDING') === 'SELECTED',
  )

  // PATCH the bid's decision state only. No cross-module writes. On success the
  // ['bids'] query is invalidated so the persisted decision survives reload.
  const decisionMutation = useMutation({
    mutationFn: async (payload: { decisionStatus: string; riskAcknowledged?: boolean; decisionNotes?: string }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/bids/${bid.id}/decision`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error((data as any)?.error || `decision_failed_${res.status}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bids', projectId] })
      setDecisionIntent(null)
      setDecisionError(null)
      setDecisionFlash(isEs ? NBA3_COPY.decisionSaved.es : NBA3_COPY.decisionSaved.en)
      setTimeout(() => setDecisionFlash(null), 2500)
    },
    onError: () => {
      setDecisionError(isEs ? NBA3_COPY.decisionFailed.es : NBA3_COPY.decisionFailed.en)
    },
  })

  // NBA-4: "Create draft contract" modal state. showDraftModal opens the
  // confirmation modal; draftCreatedId holds the id of the newly-created
  // contract so the "View draft contract" link can navigate to it.
  const navigate = useNavigate()
  const [showDraftModal, setShowDraftModal] = useState(false)
  const [draftCreatedId, setDraftCreatedId] = useState<string | null>(null)
  const [draftFlash, setDraftFlash] = useState<string | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)

  const draftMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/bids/${bid.id}/contract-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ lang: isEs ? 'es' : 'en' }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error((data as any)?.error || `draft_failed_${res.status}`)
      return data as { id: string; reusedExistingDraft?: boolean }
    },
    onSuccess: (contract) => {
      queryClient.invalidateQueries({ queryKey: ['bids', projectId] })
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
      setShowDraftModal(false)
      setDraftError(null)
      setDraftCreatedId(contract.id)
      // Idempotency: if the backend returned an existing draft (double-submit /
      // retry), say so plainly instead of implying a new one was created.
      const reused = contract.reusedExistingDraft === true
      setDraftFlash(
        reused
          ? (isEs ? NBA4_COPY.draftReused.es : NBA4_COPY.draftReused.en)
          : (isEs ? NBA4_COPY.draftCreated.es : NBA4_COPY.draftCreated.en),
      )
      setTimeout(() => setDraftFlash(null), 4000)
    },
    onError: () => {
      setDraftError(isEs ? NBA4_COPY.draftFailed.es : NBA4_COPY.draftFailed.en)
    },
  })

  // Open the original bid file via an authenticated fetch → Blob URL → new tab.
  // A plain <a href> would send the browser directly to the protected route
  // without an Authorization header, resulting in 401. This mirrors the
  // pattern used by DocumentsModule and ReceiptsModule.
  const openOriginalFile = async () => {
    if (!projectId) return
    setOpeningFile(true)
    setFileError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/bids/${bid.id}/file`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) {
        const json = await res.json().catch(() => ({}))
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, '_blank', 'noopener,noreferrer')
      // Revoke after 60 s — enough time for the browser tab to load the file.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch {
      setFileError(
        isEs
          ? 'No se pudo abrir la propuesta original. Intenta de nuevo.'
          : 'Could not open the original bid. Please try again.',
      )
    } finally {
      setOpeningFile(false)
    }
  }

  const statusColors: Record<string, string> = {
    RECEIVED: 'bg-sky-50 text-sky-600',
    ACCEPTED: 'bg-green-50 text-green-600',
    REJECTED: 'bg-red-50 text-red-600',
  }

  const hasRisks = bid.scopeGaps && bid.scopeGaps.length > 0

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div
        onClick={() => setExpanded(!expanded)}
        className="p-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-[13px] font-[800] text-[#1C1A17]">{bid.contractorName}</h3>
            <span className="text-[12px] font-[700] text-[#9E9890]">{t(`bids.trade.${bid.trade.toLowerCase()}`, { defaultValue: bid.trade })}</span>
          </div>
          <div className="flex items-center gap-2">
            {hasRisks && <AlertTriangle size={14} className="text-amber-500" />}
            {/* NBA-3A: persisted decision badge (Selected / Rejected / Not selected).
                Reflects the durable decisionStatus from the API → survives reload. */}
            {DECISION_BADGE[decisionStatus] && (
              <span
                data-testid="bid-decision-badge"
                className={`text-[9px] font-[800] px-2.5 py-1 rounded-full ${DECISION_BADGE[decisionStatus].colorClass}`}
              >
                {isEs ? DECISION_BADGE[decisionStatus].es : DECISION_BADGE[decisionStatus].en}
              </span>
            )}
            <span className={`text-[9px] font-[800] px-2.5 py-1 rounded-full ${statusColors[bid.status] || ''}`}>
              {t(`bids.status.${bid.status}`, { defaultValue: bid.status })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <DollarSign size={14} className="text-[#1C1A17]" />
            {(() => {
              const amt = formatMoney(bid.totalAmount ?? null, { isEs })
              return (
                <>
                  <span className="text-[16px] font-[900] text-[#1C1A17]">{amt.displayValue}</span>
                  {amt.isSuspicious && (
                    <span className="text-[9px] font-[700] text-amber-600 ml-1" data-testid="bid-amount-review-note">
                      {isEs ? 'Verificar monto' : 'Verify amount'}
                    </span>
                  )}
                </>
              )
            })()}
          </div>
          <div className="flex gap-2">
            {bid.materialsIncluded ? (
              <span className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                {t('Materials included')}
              </span>
            ) : (
              <span className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">
                {t('Labor only')}
              </span>
            )}
            {bid.timelineDays && (
              <span className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#9E9890]">
                {bid.timelineDays} {t('days')}
              </span>
            )}
            {/* pricingType alert — shown in collapsed header so builder sees it
                before expanding. Only shown for non-single types (options /
                alternates / combined) where misreading the total would matter. */}
            {pricingType && pricingType !== 'single' && (
              <span className="text-[9px] font-[800] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                {pricingType === 'options'
                  ? (isEs ? '⚠ opciones' : '⚠ options')
                  : pricingType === 'alternates'
                  ? (isEs ? 'alternos' : 'alts')
                  : (isEs ? 'combinado' : 'combined')}
              </span>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[#ECEAE4] pt-3 space-y-3">

          {/* ── Executive Summary ──────────────────────────────────────
              PR C: 2–3 sentence overview at the top of the expanded card
              so a builder can understand the bid at a glance before reading
              the detail sections below. Built from existing structured fields
              only — no data invented. Hidden for old bids without rawAiResponse
              (not enough payment/scheduling info to build a useful summary). */}
          {raw && (
            <div data-testid="bid-executive-summary" className="bg-[#F7F6F3] rounded-lg px-3 py-2.5">
              <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                {buildExecutiveSummary(bid, raw, isEs, tradeDisplayName)}
              </p>
            </div>
          )}

          {/* ── Decision / Risk Panel (NBA-1) ──────────────────────────────
              Decision-support shell — derives risk level, comparable status,
              missing info, and recommended action from EXISTING data only.
              No downstream writes. No data invented.                         */}
          <div
            data-testid="bid-decision-panel"
            className="border border-[#ECEAE4] rounded-lg px-3 py-2.5 space-y-2 bg-[#FAFAF8]"
          >
            <div>
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Revisión de decisión de propuesta' : 'Bid decision review'}
              </h4>
              <p className="text-[9px] font-[600] text-[#9E9890] mt-0.5">
                {isEs
                  ? 'Datos extraídos por IA — revisar antes de crear un contrato'
                  : 'AI-extracted bid data — verify before creating a contract'}
              </p>
            </div>

            {/* Risk level + comparable */}
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-[700] text-[#9E9890]">
                  {isEs ? 'Nivel de riesgo' : 'Risk level'}
                </span>
                <span
                  data-testid="bid-risk-level"
                  className={`text-[9px] font-[800] px-2 py-0.5 rounded-full ${RISK_LEVEL_DISPLAY[decision.riskLevel].colorClass}`}
                >
                  {isEs
                    ? RISK_LEVEL_DISPLAY[decision.riskLevel].es
                    : RISK_LEVEL_DISPLAY[decision.riskLevel].en}
                </span>
              </div>
              <span
                data-testid="bid-comparable-status"
                className={`text-[9px] font-[700] px-2 py-0.5 rounded-full ${
                  decision.isComparable
                    ? 'bg-green-50 text-green-700'
                    : 'bg-amber-50 text-amber-700'
                }`}
              >
                {decision.isComparable
                  ? (isEs ? 'Comparable' : 'Comparable')
                  : (isEs ? 'Aún no comparable' : 'Not comparable yet')}
              </span>
            </div>

            {/* Missing fields */}
            {decision.missingFields.length > 0 && (
              <div>
                <span className="text-[9px] font-[700] text-[#9E9890]">
                  {isEs ? 'Información faltante' : 'Missing information'}
                </span>
                <div className="flex flex-wrap gap-1 mt-1" data-testid="bid-missing-fields">
                  {decision.missingFields.map(field => (
                    <span
                      key={field}
                      className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#6B6660]"
                    >
                      {isEs
                        ? (MISSING_FIELD_LABELS[field]?.es ?? field)
                        : (MISSING_FIELD_LABELS[field]?.en ?? field)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended action */}
            <div>
              <span className="text-[9px] font-[700] text-[#9E9890]">
                {isEs ? 'Siguiente acción recomendada' : 'Recommended next action'}
              </span>
              <p
                data-testid="bid-recommended-action"
                className="text-[10px] font-[600] text-[#1C1A17] mt-0.5 leading-snug"
              >
                {isEs
                  ? RECOMMENDED_ACTION_COPY[decision.recommendedAction]?.es
                  : RECOMMENDED_ACTION_COPY[decision.recommendedAction]?.en}
              </p>
            </div>

            {/* Not connected disclaimer */}
            <p className="text-[9px] font-[600] text-[#9E9890] italic">
              {isEs
                ? 'Esta propuesta aún no está conectada a Contratos, Presupuesto, Pagos ni Calendario/Gantt.'
                : 'This bid is not connected to Contracts, Budget, Payments, or Calendar/Gantt yet.'}
            </p>
          </div>

          {/* ── Pricing breakdown ───────────────────────────────────────────
              Shown when there is a non-single pricingType or actual breakdown
              line items. For 'options' or 'alternates', the builder must
              understand that prices are NOT automatically combined — they must
              choose one option or decide which alternates to add.            */}
          {((pricingType && pricingType !== 'single') || breakdown.length > 0) && (
            <div data-testid="bid-pricing-breakdown-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Desglose de precio' : 'Pricing Breakdown'}
              </h4>
              {pricingType && pricingType !== 'single' && (
                <span className="inline-block text-[9px] font-[800] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 mb-2">
                  {pricingType === 'options'
                    ? (isEs ? '⚠ Opciones — elegir una sola' : '⚠ Options — choose one, not combined')
                    : pricingType === 'alternates'
                    ? (isEs ? 'Alternos — precio base + opciones adicionales' : 'Alternates — base price + optional add-ons')
                    : (isEs ? 'Combinado — múltiples sistemas incluidos' : 'Combined — multiple systems/phases')}
                </span>
              )}
              {breakdown.length > 0 && (
                <div className="space-y-1 mt-1">
                  {/* Raw line-item descriptions/option labels are original extracted
                      text (e.g. "OPTION A – ELECTRIC"); label them in Spanish mode. */}
                  <OriginalExtractedLabel isEs={isEs} />
                  {breakdown.map((item, i) => (
                    <div key={i} className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {item.optionLabel && (
                          <span className="text-[9px] font-[800] text-amber-600 uppercase mr-1.5">{item.optionLabel}</span>
                        )}
                        <span className="text-[10px] font-[600] text-[#1C1A17]">{item.description}</span>
                        {item.quantity != null && item.unit && (
                          <span className="text-[9px] font-[600] text-[#9E9890] ml-1">({item.quantity} {item.unit})</span>
                        )}
                      </div>
                      {item.subtotal != null && (
                        <span className="text-[10px] font-[800] text-[#1C1A17] whitespace-nowrap shrink-0">
                          {formatMoneyValue(item.subtotal, isEs)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Scope (merged) ──────────────────────────────────────────
              PR C: Scope Included and Scope Excluded merged into one section
              with compact rows. Green pills = included, red pills = excluded.
              Common EN construction terms translated to ES via dictionary.
              Unknown technical terms (PEX, LVL, etc.) preserved as-is.     */}
          {(bid.scopeIncluded.length > 0 || bid.scopeExcluded.length > 0) && (
            <div data-testid="bid-scope-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Alcance' : 'Scope'}
              </h4>
              {bid.scopeIncluded.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {bid.scopeIncluded.map((s, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] font-[600] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                      <CheckCircle size={10} />
                      {translateScopeItem(s, isEs)}
                    </span>
                  ))}
                </div>
              )}
              {bid.scopeExcluded.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {bid.scopeExcluded.map((s, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] font-[600] px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                      <XCircle size={10} />
                      {translateScopeItem(s, isEs)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Payment Terms ───────────────────────────────────────────
              PR C: Only render rows that have values. When ALL payment fields
              are null (common for simple bids), show a single compact fallback
              line instead of three rows of "Not stated" visual noise.
              Section is gated on raw (hidden for old pre-PR-208 bids).      */}
          {raw && (
            <div data-testid="bid-payment-terms-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Condiciones de pago' : 'Payment Terms'}
              </h4>
              {hasAnyPayment ? (
                <div className="space-y-1">
                  {(raw.depositPercent != null || raw.depositAmount != null) && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Depósito' : 'Deposit'}</span>
                      <span className="font-[600] text-[#1C1A17]">
                        {[
                          raw.depositPercent != null ? `${raw.depositPercent}%` : null,
                          raw.depositAmount   != null ? formatMoneyValue(raw.depositAmount, isEs) : null,
                        ].filter(Boolean).join(' — ')}
                      </span>
                    </div>
                  )}
                  {raw.paymentMethod != null && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Método' : 'Method'}</span>
                      <span className="font-[600] text-[#1C1A17]">{translatePaymentMethod(raw.paymentMethod, isEs)}</span>
                    </div>
                  )}
                  {raw.drawSchedule != null && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Programa' : 'Schedule'}</span>
                      <span className="font-[600] text-[#1C1A17]">{raw.drawSchedule}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[10px] font-[600] text-[#9E9890]">
                  {isEs ? `Condiciones de pago — ${notStated}.` : `Payment terms — ${notStated}.`}
                </p>
              )}
            </div>
          )}

          {/* ── Scheduling ──────────────────────────────────────────────
              PR C: Same suppress-empty pattern as Payment Terms.
              Only rows with values are rendered; single compact fallback
              when all scheduling fields are null.
              timelineDays is a top-level Bid field (always available).
              availableDate and validUntil come from rawAiResponse.         */}
          {raw && (
            <div data-testid="bid-scheduling-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Programación' : 'Scheduling'}
              </h4>
              {hasAnyScheduling ? (
                <div className="space-y-1">
                  {bid.timelineDays != null && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Plazo' : 'Timeline'}</span>
                      <span className="font-[600] text-[#1C1A17]">{bid.timelineDays} {isEs ? 'días' : 'days'}</span>
                    </div>
                  )}
                  {raw.availableDate != null && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Disponible' : 'Available'}</span>
                      <span className="font-[600] text-[#1C1A17]">{raw.availableDate}</span>
                    </div>
                  )}
                  {raw.validUntil != null && (
                    <div className="flex gap-2 text-[10px]">
                      <span className="font-[700] text-[#9E9890] w-[68px] shrink-0">{isEs ? 'Expira' : 'Expires'}</span>
                      <span className="font-[600] text-[#1C1A17]">{raw.validUntil}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[10px] font-[600] text-[#9E9890]">
                  {isEs ? `Programación — ${notStated}.` : `Scheduling — ${notStated}.`}
                </p>
              )}
            </div>
          )}

          {/* ── Materials & Fixtures ────────────────────────────────────
              PR C: Only shows rawAiResponse-specific details that are NOT
              already visible in the collapsed header (materialsIncluded and
              laborOnly are already shown there as badges). Expanded section
              focuses on the new rawAiResponse fields: fixturesIncluded,
              warrantyMonths, insuranceMentioned, licenseNumber.
              Section is hidden entirely when none of these have data.       */}
          {raw && (raw.fixturesIncluded != null || raw.warrantyMonths != null || raw.insuranceMentioned || raw.licenseNumber) && (
            <div data-testid="bid-materials-fixtures-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Materiales y equipo' : 'Materials & Fixtures'}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {raw.fixturesIncluded != null && (
                  <span className={`flex items-center gap-1 text-[9px] font-[700] px-2 py-0.5 rounded-full ${raw.fixturesIncluded ? 'bg-green-50 text-green-700' : 'bg-[#F2F0EB] text-[#9E9890]'}`}>
                    {raw.fixturesIncluded ? <CheckCircle size={10} /> : <XCircle size={10} />}
                    {isEs ? 'Accesorios' : 'Fixtures'}
                  </span>
                )}
                {raw.warrantyMonths != null && (
                  <span className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600">
                    {raw.warrantyMonths} {isEs ? 'meses garantía' : 'mo warranty'}
                  </span>
                )}
                {raw.insuranceMentioned && (
                  <span className="flex items-center gap-1 text-[9px] font-[700] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                    <CheckCircle size={10} />
                    {isEs ? 'Seguro mencionado' : 'Insurance noted'}
                  </span>
                )}
                {raw.licenseNumber && (
                  <span className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#9E9890]">
                    Lic. {raw.licenseNumber}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── Allowances ──────────────────────────────────────────────
              Stated dollar allowances (e.g. "$2,000 fixture allowance").
              Section is hidden when allowances is empty / absent.          */}
          {allowances.length > 0 && (
            <div data-testid="bid-allowances-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Partidas estimadas' : 'Allowances'}
              </h4>
              {/* Allowance strings are original extracted text — label in ES mode. */}
              <OriginalExtractedLabel isEs={isEs} />
              <div className="flex flex-wrap gap-1.5">
                {allowances.map((a, i) => (
                  <span key={i} className="text-[10px] font-[600] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Alerts (merged) ─────────────────────────────────────────
              PR C: Combines system-computed scopeGaps (Risk Alerts) and
              AI-extracted rawAiResponse.redFlags into a single section.
              AI red flags are grouped by category so critical risks (no
              insurance, high deposit) stand out in red while informational
              items (missing info) show in muted gray. Eliminates the
              visual alarm fatigue of uniform red paragraphs.              */}
          {(hasRisks || redFlags.length > 0) && (
            <div data-testid="bid-alerts-section">
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1.5">
                {isEs ? 'Alertas' : 'Alerts'}
              </h4>
              {/* Scope gaps from system analysis (if any) — localized for display */}
              {hasRisks && bid.scopeGaps!.map((gap, i) => (
                <div key={`gap-${i}`} className="flex items-start gap-2 py-0.5">
                  <AlertTriangle size={12} className={`shrink-0 mt-0.5 ${gap.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                  <RiskNoteText note={gap.gap} isEs={isEs} colorClass="text-[#1C1A17]" />
                </div>
              ))}
              {/* AI red flags grouped by category — localized for display */}
              {groupedFlags.map(group => (
                <div key={group.category} className="mt-1.5 first:mt-0">
                  <span className={`text-[9px] font-[800] uppercase tracking-[0.5px] ${group.headerColorClass}`}>
                    {group.label}
                  </span>
                  {group.flags.map((flag, i) => (
                    <div key={i} className="flex items-start gap-2 py-0.5 pl-2 mt-0.5">
                      {group.category === 'critical'
                        ? <AlertTriangle size={11} className="text-red-500 shrink-0 mt-0.5" />
                        : group.category === 'missing'
                        ? <span className="w-[11px] shrink-0 mt-0.5 text-[9px] text-[#9E9890] leading-none">—</span>
                        : <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-0.5" />
                      }
                      <RiskNoteText note={flag} isEs={isEs} colorClass={group.flagColorClass} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* ── Contractor's notes ──────────────────────────────────────
              PR C: bid.notes shows the contractor's original wording (may
              be in Spanish, English, or a mix). Labeled clearly to
              distinguish it from AI-extracted or system-generated content.
              Original text preserved — not translated in this PR.         */}
          {bid.notes && (
            <div>
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1">
                {isEs ? 'Notas del contratista' : "Contractor's notes"}
              </h4>
              {/* The contractor's note is original extracted text (often English);
                  label it in Spanish mode without translating or mutating it. */}
              <OriginalExtractedLabel isEs={isEs} />
              <p className="text-[10px] font-[600] text-[#9E9890] bg-[#F2F0EB] rounded-lg p-2.5 italic">
                {bid.notes}
              </p>
            </div>
          )}

          {/* Source file button — only shown when original file was preserved.
              Uses authenticated fetch → Blob URL → window.open to open the
              file in a new tab without exposing the protected route URL
              directly. A plain <a href> would bypass the Bearer token and
              receive 401. Mirrors DocumentsModule / ReceiptsModule pattern.
              Satisfies the product rule: AI extraction must not replace
              source preservation. Always the last action in the expanded card. */}
          {bid.storagePath && projectId && (
            <div className="flex flex-col gap-1 mt-1">
              <button
                type="button"
                onClick={openOriginalFile}
                disabled={openingFile}
                className="inline-flex items-center gap-1.5 text-[10px] font-[700] text-[#06539B] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bid-view-original-file"
              >
                {openingFile
                  ? <Loader2 size={11} className="animate-spin" />
                  : <FileText size={11} />}
                {isEs ? 'Ver propuesta original' : 'View original bid'}
              </button>
              {fileError && (
                <p className="text-[10px] font-[600] text-red-600" data-testid="bid-file-error">
                  {fileError}
                </p>
              )}
            </div>
          )}

          {/* ── Future Actions Panel (NBA-1) ────────────────────────────────
              Decision-support only. All buttons are DISABLED placeholders —
              none execute downstream writes in NBA-1. No contract, payment,
              budget, calendar, or gantt records are created here.            */}
          <div
            data-testid="bid-future-actions-panel"
            className="border border-dashed border-[#ECEAE4] rounded-lg px-3 py-2.5 space-y-2"
          >
            <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {isEs ? 'Próximas acciones' : 'Next actions'}
            </h4>
            <p
              data-testid="bid-future-actions-coming-soon"
              className="text-[9px] font-[600] text-[#9E9890]"
            >
              {isEs
                ? 'Próximamente — requiere flujo de confirmación'
                : 'Coming soon — requires confirmation flow'}
            </p>

            {/* NBA-3B: persisted decision metadata — who/when, risk ack, and the
                decision note, shown whenever a durable decision (or note) exists.
                Read-only here; editing happens in the confirmation modal. It also
                restates that this is a decision state only, with no contract. */}
            {(decisionStatus === 'SELECTED' || decisionStatus === 'REJECTED' || bid.decisionNotes) && (
              <div
                data-testid="bid-decision-metadata"
                className="rounded-lg bg-[#FAFAF8] border border-[#ECEAE4] px-2.5 py-2 space-y-1"
              >
                {formatDecisionMeta(decisionStatus, bid.decisionAt, bid.decisionBy, isEs) && (
                  <p data-testid="bid-decision-meta-byon" className="text-[10px] font-[700] text-[#1C1A17]">
                    {formatDecisionMeta(decisionStatus, bid.decisionAt, bid.decisionBy, isEs)}
                  </p>
                )}
                {bid.riskAcknowledgedAt && (
                  <p data-testid="bid-decision-meta-riskack" className="text-[9px] font-[700] text-amber-700 flex items-center gap-1">
                    <AlertTriangle size={10} /> {isEs ? NBA3_COPY.riskAcknowledged.es : NBA3_COPY.riskAcknowledged.en}
                  </p>
                )}
                {bid.decisionNotes && (
                  <p data-testid="bid-decision-meta-note" className="text-[10px] font-[600] text-[#6B6660]">
                    <span className="font-[800] text-[#9E9890]">
                      {isEs ? NBA3_COPY.decisionNote.es : NBA3_COPY.decisionNote.en}:{' '}
                    </span>
                    {bid.decisionNotes}
                  </p>
                )}
                <p data-testid="bid-decision-meta-stateonly" className="text-[9px] font-[600] text-[#9E9890] border-t border-[#ECEAE4] pt-1">
                  {(bid.hasDraftContract || !!draftCreatedId)
                    ? (isEs ? NBA3_COPY.draftContractExists.es : NBA3_COPY.draftContractExists.en)
                    : `${isEs ? NBA3_COPY.decisionStateOnly.es : NBA3_COPY.decisionStateOnly.en} · ${isEs ? NBA3_COPY.bidContractFuture.es : NBA3_COPY.bidContractFuture.en}`}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {FUTURE_ACTIONS.map(action => {
                // NBA-2: "Ask for clarification" opens a reviewable FE-only draft.
                if (action.key === 'ask_clarification') {
                  return (
                    <button
                      key={action.key}
                      type="button"
                      data-testid="bid-ask-clarification-btn"
                      onClick={() => setShowClarification(true)}
                      className="inline-flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-lg border border-[#06539B] bg-[#EFF6FF] text-[#06539B] hover:bg-[#06539B] hover:text-white transition-colors"
                    >
                      <MessageSquare size={10} />
                      {isEs ? action.es : action.en}
                    </button>
                  )
                }
                // NBA-3A: "Mark as selected" — opens a confirmation modal that
                // persists decisionStatus only. No downstream record is created
                // (no contract, payment, budget, calendar, or contact). Label
                // flips to "Not selected" once selected (clicking again clears
                // the selection back to PENDING).
                if (action.key === 'mark_selected') {
                  const isSelected = decisionStatus === 'SELECTED'
                  return (
                    <button
                      key={action.key}
                      type="button"
                      data-testid="bid-mark-selected-btn"
                      onClick={() =>
                        isSelected
                          ? decisionMutation.mutate({ decisionStatus: 'PENDING' })
                          : setDecisionIntent('SELECTED')
                      }
                      className={`inline-flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-green-600 bg-green-600 text-white hover:bg-green-700'
                          : 'border-green-600 bg-green-50 text-green-700 hover:bg-green-600 hover:text-white'
                      }`}
                    >
                      <CheckCircle size={10} />
                      {isSelected
                        ? (isEs ? NBA3_COPY.selected.es : NBA3_COPY.selected.en)
                        : (isEs ? NBA3_COPY.markAsSelected.es : NBA3_COPY.markAsSelected.en)}
                    </button>
                  )
                }
                // NBA-3A: "Reject" — opens a confirmation modal. Does NOT delete
                // the bid and creates no downstream records. Clicking again on a
                // rejected bid clears it back to PENDING.
                if (action.key === 'reject') {
                  const isRejected = decisionStatus === 'REJECTED'
                  return (
                    <button
                      key={action.key}
                      type="button"
                      data-testid="bid-reject-btn"
                      onClick={() =>
                        isRejected
                          ? decisionMutation.mutate({ decisionStatus: 'PENDING' })
                          : setDecisionIntent('REJECTED')
                      }
                      className={`inline-flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-lg border transition-colors ${
                        isRejected
                          ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
                          : 'border-red-300 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white'
                      }`}
                    >
                      <XCircle size={10} />
                      {isRejected
                        ? (isEs ? NBA3_COPY.rejected.es : NBA3_COPY.rejected.en)
                        : (isEs ? NBA3_COPY.reject.es : NBA3_COPY.reject.en)}
                    </button>
                  )
                }
                // NBA-4: "Create draft contract" — active ONLY for SELECTED bids,
                // on real (non-demo) projects. Opens a confirmation modal that
                // states plainly what is and is not created. Creates NO payment,
                // Budget, Calendar/Gantt, Contact, or any other downstream record.
                if (action.key === 'create_contract') {
                  if (decisionStatus !== 'SELECTED' || !projectId || isDemoProject(projectId)) {
                    return (
                      <button
                        key={action.key}
                        type="button"
                        disabled
                        data-testid="bid-create-draft-btn-disabled"
                        className="text-[9px] font-[700] px-2.5 py-1 rounded-lg border border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed"
                      >
                        {isEs ? action.es : action.en}
                      </button>
                    )
                  }
                  return (
                    <button
                      key={action.key}
                      type="button"
                      data-testid="bid-create-draft-btn"
                      onClick={() => setShowDraftModal(true)}
                      className="inline-flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-lg border border-[#00BCD4] bg-[#E0F7FA] text-[#006064] hover:bg-[#00BCD4] hover:text-white transition-colors"
                    >
                      <FilePlus size={10} />
                      {isEs ? action.es : action.en}
                    </button>
                  )
                }
                // Every other future action stays a disabled placeholder.
                return (
                  <button
                    key={action.key}
                    type="button"
                    disabled
                    className="text-[9px] font-[700] px-2.5 py-1 rounded-lg border border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed"
                  >
                    {isEs ? action.es : action.en}
                  </button>
                )
              })}
            </div>

            {/* NBA-3A: decision save feedback — inline, no native dialogs. */}
            {decisionFlash && (
              <p data-testid="bid-decision-flash" className="text-[9px] font-[800] text-green-700 flex items-center gap-1">
                <Check size={10} /> {decisionFlash}
              </p>
            )}
            {decisionError && (
              <p data-testid="bid-decision-error" className="text-[9px] font-[800] text-red-600">
                {decisionError}
              </p>
            )}

            {/* NBA-4: draft creation feedback — inline, no native dialogs. */}
            {draftFlash && (
              <div data-testid="bid-draft-flash" className="flex items-center gap-1.5 flex-wrap">
                <p className="text-[9px] font-[800] text-[#006064] flex items-center gap-1">
                  <Check size={10} /> {draftFlash}
                </p>
                {draftCreatedId && projectId && (
                  <button
                    type="button"
                    data-testid="bid-view-draft-btn"
                    onClick={() => navigate(`/projects/${projectId}/contracts`)}
                    className="text-[9px] font-[800] px-2 py-0.5 rounded border border-[#00BCD4] text-[#006064] hover:bg-[#00BCD4] hover:text-white transition-colors"
                  >
                    {isEs ? NBA4_COPY.viewDraftContract.es : NBA4_COPY.viewDraftContract.en}
                  </button>
                )}
              </div>
            )}
            {draftError && (
              <p data-testid="bid-draft-error" className="text-[9px] font-[800] text-red-600">
                {draftError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Contacts Linking Foundation — link this bidder to a project contact.
          Real projects only. Shows whatever detected info exists (name; email/
          phone only if extraction has filled them — never invented). Linking a
          contact here creates NO contract and triggers no downstream writes. */}
      {projectId && !isDemoProject(projectId) && (
        <div className="mt-3">
          <div className="text-[10px] font-[800] uppercase tracking-wide text-[#9E9890] mb-1">
            {isEs ? 'Contacto del contratista' : 'Contractor contact'}
          </div>
          <ContactLinker
            projectId={projectId}
            entity="bid"
            entityId={bid.id}
            linkedContact={bid.contractorContact ?? null}
            roleFilter="CONTRACTOR"
            detected={{ name: bid.contractorName, email: bid.extractedEmail, phone: bid.extractedPhone }}
            isEs={isEs}
            invalidateKeys={[['bids', projectId]]}
          />
        </div>
      )}

      {/* NBA-2: Clarification draft modal — reviewable, FE-only, no send/write. */}
      {showClarification && (
        <ClarificationDraftModal
          bid={bid}
          raw={raw}
          decision={decision}
          tradeName={tradeDisplayName}
          projectName={projectName}
          isEs={isEs}
          onClose={() => setShowClarification(false)}
        />
      )}

      {/* NBA-3A: decision confirmation modal — persists decisionStatus only. */}
      {decisionIntent && (
        <BidDecisionModal
          intent={decisionIntent}
          decision={decision}
          anotherSelectedInTrade={anotherSelectedInTrade}
          initialNote={bid.decisionNotes ?? ''}
          isEs={isEs}
          saving={decisionMutation.isPending}
          onCancel={() => setDecisionIntent(null)}
          onConfirm={(riskAcknowledged, decisionNotes) =>
            decisionMutation.mutate({ decisionStatus: decisionIntent, riskAcknowledged, decisionNotes })
          }
        />
      )}

      {/* NBA-4: create draft contract confirmation modal. */}
      {showDraftModal && (
        <CreateDraftContractModal
          bid={bid}
          decision={decision}
          anotherSelectedInTrade={anotherSelectedInTrade}
          isEs={isEs}
          saving={draftMutation.isPending}
          onCancel={() => { setShowDraftModal(false); setDraftError(null) }}
          onConfirm={() => draftMutation.mutate()}
          error={draftError}
        />
      )}
    </div>
  )
}

// ── NBA-3A: BID DECISION CONFIRMATION MODAL ───────
// Confirms a durable "Mark as selected" or "Reject" decision before it is
// persisted. It states plainly that NO contract, budget, payments, calendar,
// gantt, or contact record is created and that Bid → Contract is a future step.
// When SELECTING a risky / missing-info / not-comparable bid, the builder must
// tick a risk-acknowledgement checkbox before Confirm enables — the
// acknowledgement is then recorded with the decision. No native dialogs:
// everything is inline. Mobile-safe (bottom sheet on small, centered on wide).
function BidDecisionModal({
  intent, decision, anotherSelectedInTrade, initialNote, isEs, saving, onCancel, onConfirm,
}: {
  intent: 'SELECTED' | 'REJECTED'
  decision: BidDecision
  anotherSelectedInTrade: boolean
  initialNote: string
  isEs: boolean
  saving: boolean
  onCancel: () => void
  onConfirm: (riskAcknowledged: boolean, decisionNotes: string) => void
}) {
  const c = (k: keyof typeof NBA3_COPY) => (isEs ? NBA3_COPY[k].es : NBA3_COPY[k].en)
  const isSelect = intent === 'SELECTED'

  // A selection is "risky" when the bid is high/needs-review risk, has any
  // missing info, or is not comparable yet. Reject never requires acknowledgement.
  const isRisky =
    isSelect &&
    (decision.riskLevel === 'high' ||
      decision.riskLevel === 'needs_review' ||
      decision.missingFields.length > 0 ||
      !decision.isComparable)

  const [acknowledged, setAcknowledged] = useState(false)
  // NBA-3B: optional decision note (never required). Prefilled with any existing
  // note so the builder can edit it. Persisted via the existing decisionNotes field.
  const [note, setNote] = useState(initialNote)
  const confirmDisabled = saving || (isRisky && !acknowledged)

  return (
    <div
      data-testid="bid-decision-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-[14px] font-[900] text-[#1C1A17] flex items-center gap-1.5">
              {isSelect
                ? <CheckCircle size={15} className="text-green-600" />
                : <XCircle size={15} className="text-red-600" />}
              {isSelect ? c('markAsSelected') : c('reject')}
            </h3>
            <button
              type="button"
              data-testid="bid-decision-cancel-x"
              onClick={onCancel}
              className="text-[#9E9890] hover:text-[#1C1A17] transition-colors"
              aria-label={c('cancel')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Confirmation prompt */}
          <p className="text-[11px] font-[700] text-[#1C1A17]">
            {isSelect ? c('selectionRequiresConfirmation') : c('rejectRequiresConfirmation')}
          </p>

          {/* Risk / missing-info warning (selection only) */}
          {isRisky && (
            <div
              data-testid="bid-decision-warning"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 space-y-1.5"
            >
              <p className="text-[10px] font-[800] text-amber-700 flex items-center gap-1">
                <AlertTriangle size={11} /> {c('bidHasMissingInfo')} {c('selectAnyway')}
              </p>
              <label className="flex items-start gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="bid-decision-risk-ack"
                  checked={acknowledged}
                  onChange={e => setAcknowledged(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                />
                <span className="text-[10px] font-[700] text-amber-800">{c('riskAcknowledged')}</span>
              </label>
            </div>
          )}

          {/* Another bid already selected in this trade (non-blocking, select only).
              NBA-3B: state plainly that selecting this one does NOT unselect the
              other — more than one bid may stay selected until contract creation. */}
          {isSelect && anotherSelectedInTrade && (
            <div
              data-testid="bid-decision-trade-warning"
              className="rounded-lg border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 space-y-1"
            >
              <p className="text-[10px] font-[700] text-amber-700 flex items-start gap-1">
                <Info size={11} className="mt-0.5 shrink-0" /> {c('alreadySelectedInTrade')}
              </p>
              <p data-testid="bid-decision-multiple-note" className="text-[9px] font-[600] text-amber-700/90 pl-[15px]">
                {c('multipleMaySelect')}
              </p>
            </div>
          )}

          {/* Reject-only reassurance: the bid is not deleted */}
          {!isSelect && (
            <p data-testid="bid-decision-reject-note" className="text-[10px] font-[600] text-[#6B6660]">
              {c('rejectDoesNotDelete')}
            </p>
          )}

          {/* NBA-3B: optional decision note (never required). */}
          <div className="space-y-1">
            <label
              htmlFor="bid-decision-note-input"
              className="block text-[10px] font-[800] text-[#6B6660]"
            >
              {c('addDecisionNote')}
            </label>
            <textarea
              id="bid-decision-note-input"
              data-testid="bid-decision-note-input"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={c('optionalDecisionNote')}
              rows={2}
              maxLength={2000}
              className="w-full text-[11px] font-[600] text-[#1C1A17] rounded-lg border border-[#ECEAE4] px-2.5 py-1.5 resize-y focus:outline-none focus:border-[#06539B]"
            />
          </div>

          {/* Safety disclaimers — no downstream writes */}
          <ul data-testid="bid-decision-disclaimer" className="space-y-1 border-t border-[#ECEAE4] pt-2.5">
            <li data-testid="bid-decision-state-only" className="text-[9px] font-[600] text-[#9E9890]">• {c('decisionStateOnly')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('noContract')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('noBudgetPaymentsCal')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('bidContractFuture')}</li>
          </ul>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="bid-decision-cancel"
              onClick={onCancel}
              className="text-[10px] font-[800] px-3 py-1.5 rounded-lg border border-[#ECEAE4] text-[#6B6660] hover:bg-[#FAFAF8] transition-colors"
            >
              {c('cancel')}
            </button>
            <button
              type="button"
              data-testid="bid-decision-confirm"
              disabled={confirmDisabled}
              onClick={() => onConfirm(isRisky ? acknowledged : false, note.trim())}
              className={`inline-flex items-center gap-1 text-[10px] font-[800] px-3 py-1.5 rounded-lg border transition-colors ${
                confirmDisabled
                  ? 'border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed'
                  : isSelect
                    ? 'border-green-600 bg-green-600 text-white hover:bg-green-700'
                    : 'border-red-600 bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {saving && <Loader2 size={11} className="animate-spin" />}
              {saving ? c('saving') : c('confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── NBA-4: CREATE DRAFT CONTRACT CONFIRMATION MODAL ───────────────────────────
// Shows exactly what WILL and WILL NOT be created when the builder confirms.
// No silent writes. The builder sees: EN/ES disclaimers, Source: selected bid,
// contact warnings (blocking if missing, non-blocking if no email), risk warning
// if bid has missing info or high risk, multi-trade warning if applicable.
// Mobile-safe (bottom sheet on small, centered on wide). No native dialogs.
function CreateDraftContractModal({
  bid, decision, anotherSelectedInTrade, isEs, saving, onCancel, onConfirm, error,
}: {
  bid: Bid
  decision: BidDecision
  anotherSelectedInTrade: boolean
  isEs: boolean
  saving: boolean
  onCancel: () => void
  onConfirm: () => void
  error: string | null
}) {
  const c = (k: keyof typeof NBA4_COPY) => (isEs ? NBA4_COPY[k].es : NBA4_COPY[k].en)

  // Non-blocking: no linked contractor contact. The draft can still be created —
  // contractorName falls back to the bid's contractor name server-side. We only
  // warn the builder to link/select a contact before SENDING. We never create or
  // link a contact automatically.
  const hasLinkedContact = !!bid.contractorContactId
  // Non-blocking: linked contact exists but has no email — can create draft but
  // user must add an email before sending.
  const linkedContactHasNoEmail = hasLinkedContact && !bid.contractorContact?.email
  // Risk warning: high/needs-review bid or missing fields.
  const isRisky =
    decision.riskLevel === 'high' ||
    decision.riskLevel === 'needs_review' ||
    decision.missingFields.length > 0

  // Confirm is blocked ONLY while the request is in flight. A missing contact is
  // a warning, NOT a blocker (a selected bid always carries a contractorName).
  const confirmDisabled = saving

  return (
    <div
      data-testid="bid-create-draft-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-[14px] font-[900] text-[#1C1A17] flex items-center gap-1.5">
              <FilePlus size={15} className="text-[#006064]" />
              {c('createDraftContract')}
            </h3>
            <button
              type="button"
              data-testid="bid-create-draft-cancel-x"
              onClick={onCancel}
              className="text-[#9E9890] hover:text-[#1C1A17] transition-colors"
              aria-label={c('cancel')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Source attribution */}
          <p data-testid="bid-create-draft-source" className="text-[10px] font-[800] text-[#006064]">
            {c('sourceSelectedBid')}
          </p>

          {/* Non-blocking: no linked contractor contact. Warns but does NOT
              block — the draft can be created from the bid's contractor name. */}
          {!hasLinkedContact && (
            <div
              data-testid="bid-create-draft-no-contact-warning"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
            >
              <p className="text-[10px] font-[700] text-amber-800 flex items-start gap-1">
                <Info size={11} className="mt-0.5 shrink-0" />
                {c('noLinkedContact')}
              </p>
            </div>
          )}

          {/* Non-blocking: linked contact has no email */}
          {linkedContactHasNoEmail && (
            <div
              data-testid="bid-create-draft-no-email-warning"
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
            >
              <p className="text-[10px] font-[700] text-amber-800 flex items-start gap-1">
                <Info size={11} className="mt-0.5 shrink-0" />
                {c('linkedContactNoEmail')}
              </p>
            </div>
          )}

          {/* Non-blocking: bid has risk/missing info */}
          {isRisky && (
            <div
              data-testid="bid-create-draft-risk-warning"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
            >
              <p className="text-[10px] font-[700] text-amber-700 flex items-start gap-1">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                {c('riskWarning')}
              </p>
            </div>
          )}

          {/* Non-blocking: multiple bids selected in this trade */}
          {anotherSelectedInTrade && (
            <div
              data-testid="bid-create-draft-multi-trade-warning"
              className="rounded-lg border border-amber-200 bg-amber-50/60 px-2.5 py-1.5"
            >
              <p className="text-[10px] font-[700] text-amber-700 flex items-start gap-1">
                <Info size={11} className="mt-0.5 shrink-0" />
                {c('multipleSelectedWarning')}
              </p>
            </div>
          )}

          {/* Safety disclaimers — what is and is NOT created */}
          <ul data-testid="bid-create-draft-disclaimer" className="space-y-1 border-t border-[#ECEAE4] pt-2.5">
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('draftOnly')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('doesNotSend')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('doesNotCreatePayments')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('doesNotUpdateBudget')}</li>
            <li className="text-[9px] font-[600] text-[#9E9890]">• {c('doesNotCreateCalGantt')}</li>
            <li data-testid="bid-create-draft-review-note" className="text-[9px] font-[700] text-[#1C1A17]">• {c('reviewBeforeSending')}</li>
          </ul>

          {/* Inline error from the mutation */}
          {error && (
            <p data-testid="bid-create-draft-error-inline" className="text-[9px] font-[800] text-red-600">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="bid-create-draft-cancel"
              onClick={onCancel}
              className="text-[10px] font-[800] px-3 py-1.5 rounded-lg border border-[#ECEAE4] text-[#6B6660] hover:bg-[#FAFAF8] transition-colors"
            >
              {c('cancel')}
            </button>
            <button
              type="button"
              data-testid="bid-create-draft-confirm"
              disabled={confirmDisabled}
              onClick={onConfirm}
              className={`inline-flex items-center gap-1 text-[10px] font-[800] px-3 py-1.5 rounded-lg border transition-colors ${
                confirmDisabled
                  ? 'border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed'
                  : 'border-[#006064] bg-[#006064] text-white hover:bg-[#004D40]'
              }`}
            >
              {saving && <Loader2 size={11} className="animate-spin" />}
              {saving ? c('creating') : c('confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── NBA-2: CLARIFICATION DRAFT MODAL ──────────────
// Renders a reviewable clarification-request draft built from the bid's
// missing/unclear info. FE-only: the body is editable, can be copied to the
// clipboard, and NOTHING is sent or persisted. Contractor bids carry no email
// (see ContractorBid schema), so a "no contractor email found" warning is shown
// and the Save/Send affordances stay disabled — the user copies the text
// manually instead. No native dialogs; clipboard failure surfaces inline.
function ClarificationDraftModal({
  bid, raw, decision, tradeName, projectName, isEs, onClose,
}: {
  bid: Bid
  raw: BidRawAi | null
  decision: BidDecision
  tradeName: string
  projectName: string | null
  isEs: boolean
  onClose: () => void
}) {
  const c = (k: keyof typeof NBA2_COPY) => (isEs ? NBA2_COPY[k].es : NBA2_COPY[k].en)

  // Build the draft from missing-info signals only (NBA-1 missingFields +
  // red-flag-derived topics). Never invents details.
  const extraTopics = deriveClarificationTopics(raw)
  const draft = buildClarificationDraft(
    {
      contractorName: bid.contractorName,
      tradeName,
      projectName,
      missingFields: decision.missingFields,
      extraTopics,
    },
    isEs,
  )

  const [bodyText, setBodyText] = useState(draft.body)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)

  // Contractor bids store no email/contact — the recipient is always missing.
  const recipientEmail: string | null = null
  const hasRecipient = !!recipientEmail

  const copyMessage = async () => {
    const text = `${draft.subject}\n\n${bodyText}`
    setCopyError(null)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyError(
        isEs ? 'No se pudo copiar. Selecciona y copia el texto manualmente.'
             : 'Could not copy. Select and copy the text manually.',
      )
    }
  }

  return (
    <div
      data-testid="bid-clarification-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2 sticky top-0 bg-white border-b border-[#ECEAE4]">
          <div>
            <h3 className="text-[14px] font-[900] text-[#1C1A17]">{c('clarificationDraft')}</h3>
            <p className="text-[10px] font-[700] text-amber-600 mt-0.5">{c('reviewBeforeSending')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isEs ? 'Cerrar' : 'Close'}
            className="text-[#9E9890] hover:text-[#1C1A17] shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          {/* Missing info used to build the draft */}
          <div>
            <span className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {c('missingInfoToRequest')}
            </span>
            {draft.hasMissingInfo ? (
              <div className="flex flex-wrap gap-1 mt-1" data-testid="bid-clarification-topics">
                {draft.topicKeys.map(k => (
                  <span key={k} className="text-[9px] font-[700] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#6B6660]">
                    {clarificationTopicLabel(k, isEs)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] font-[600] text-[#6B6660] mt-1" data-testid="bid-clarification-none">
                {c('noClarificationNeeded')}
              </p>
            )}
          </div>

          {/* Subject (read-only) */}
          <div>
            <span className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {c('subjectLabel')}
            </span>
            <p className="text-[11px] font-[700] text-[#1C1A17] mt-0.5" data-testid="bid-clarification-subject">
              {draft.subject}
            </p>
          </div>

          {/* Editable body */}
          <div>
            <span className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {c('editDraft')}
            </span>
            <textarea
              data-testid="bid-clarification-body"
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              rows={12}
              className="w-full mt-1 text-[11px] font-[500] text-[#1C1A17] leading-relaxed border border-[#ECEAE4] rounded-lg p-2.5 focus:outline-none focus:border-[#06539B] resize-y"
            />
          </div>

          {/* Missing contractor email warning (bids carry no email) */}
          {!hasRecipient && (
            <div
              data-testid="bid-clarification-no-email"
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
            >
              <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-[10px] font-[700] text-amber-700 leading-snug">
                {c('noContractorEmail')}.{' '}
                <span className="font-[600]">{c('addSelectContact')}.</span>
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="bid-clarification-copy"
              onClick={copyMessage}
              className="inline-flex items-center gap-1.5 text-[10px] font-[800] px-3 py-1.5 rounded-lg bg-[#06539B] text-white hover:bg-[#04437f] transition-colors"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? c('copied') : c('copyMessage')}
            </button>
            {/* Save/Send are disabled — FE-only draft, no recipient, no write. */}
            <button
              type="button"
              disabled
              title={c('addSelectContact')}
              className="text-[10px] font-[700] px-3 py-1.5 rounded-lg border border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed"
            >
              {c('saveDraft')}
            </button>
            <button
              type="button"
              disabled
              title={c('addSelectContact')}
              className="text-[10px] font-[700] px-3 py-1.5 rounded-lg border border-[#ECEAE4] text-[#9E9890] opacity-50 cursor-not-allowed"
            >
              {c('sendLater')}
            </button>
          </div>
          {copyError && (
            <p className="text-[10px] font-[600] text-red-600" data-testid="bid-clarification-copy-error">{copyError}</p>
          )}

          {/* Safety disclaimers — no auto-send, no cross-module writes */}
          <div
            data-testid="bid-clarification-disclaimer"
            className="border-t border-[#ECEAE4] pt-2.5 space-y-0.5"
          >
            <p className="text-[9px] font-[800] text-amber-600">• {c('doNotSendAuto')}</p>
            <p className="text-[9px] font-[600] text-[#9E9890]">• {c('noContract')}</p>
            <p className="text-[9px] font-[600] text-[#9E9890]">• {c('noBudgetPaymentsCal')}</p>
            <p className="text-[9px] font-[600] text-[#9E9890]">• {c('responseReviewable')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Bilingual label for a clarification topic chip (shown as "which missing items
// were used"). Reuses NBA-1 missing-field labels where they overlap.
function clarificationTopicLabel(key: string, isEs: boolean): string {
  if (MISSING_FIELD_LABELS[key]) return isEs ? MISSING_FIELD_LABELS[key].es : MISSING_FIELD_LABELS[key].en
  const EXTRA: Record<string, { en: string; es: string }> = {
    insurance:          { en: 'Insurance / License', es: 'Seguro / Licencia' },
    pricing:            { en: 'Pricing clarity',     es: 'Claridad de precio' },
    materials_fixtures: { en: 'Materials / Fixtures', es: 'Materiales / Accesorios' },
    expiration:         { en: 'Bid expiration',      es: 'Expiración de propuesta' },
  }
  return EXTRA[key] ? (isEs ? EXTRA[key].es : EXTRA[key].en) : key
}
